/**
 * Contribution Agent Widget
 *
 * Floating bottom-right bubble → chat panel.
 * Features: screen context tracking, screenshot drag-and-drop,
 * bug reports, feature suggestions, navigation help, points display.
 */

import { useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';

const RegionSelector = lazy(() => import('./RegionSelector.jsx'));

const API_URL = '/api';

async function apiPost(path, data) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

async function apiGet(path) {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  return res.json();
}

// Get human-readable page name from path with item-level context
function getPageContext(pathname, searchParams) {
  const emailId = searchParams?.get('id');
  const tab = searchParams?.get('tab');
  const folder = searchParams?.get('folder');
  const eventId = searchParams?.get('eventId');
  const dealId = searchParams?.get('deal');
  const contactId = searchParams?.get('id');

  if (pathname.startsWith('/email')) {
    const f = folder || 'inbox';
    const section = emailId ? `${f.charAt(0).toUpperCase() + f.slice(1)} / Email #${emailId.substring(0, 8)}` : f.charAt(0).toUpperCase() + f.slice(1);
    return { page: 'Email', section, path: pathname, itemId: emailId, itemType: emailId ? 'email' : null };
  }
  if (pathname.startsWith('/calendar')) {
    const section = eventId ? `Event #${eventId.substring(0, 8)}` : (tab ? tab.charAt(0).toUpperCase() + tab.slice(1) : null);
    return { page: 'Calendar', section, path: pathname, itemId: eventId, itemType: eventId ? 'event' : null };
  }
  if (pathname.startsWith('/voice')) {
    const voiceTab = searchParams?.get('tab');
    const voiceId = searchParams?.get('id');
    const section = voiceTab ? voiceTab.charAt(0).toUpperCase() + voiceTab.slice(1) + (voiceId ? ` #${voiceId.substring(0, 8)}` : '') : null;
    return { page: 'Voice', section, path: pathname, itemId: voiceId, itemType: voiceTab || null };
  }
  if (pathname.startsWith('/meet')) return { page: 'Meet', section: null, path: pathname };
  if (pathname.startsWith('/sms')) return { page: 'SMS', section: null, path: pathname };
  if (pathname.startsWith('/admin/bugs')) return { page: 'Admin', section: 'Bug Tracker', path: pathname };
  if (pathname.startsWith('/admin')) return { page: 'Admin', section: 'Settings', path: pathname };
  if (pathname.startsWith('/equity')) return { page: 'Equity', section: 'Ownership Model', path: pathname };
  if (pathname.startsWith('/account/contributions')) return { page: 'Account', section: 'Contributions', path: pathname };
  if (pathname.startsWith('/account/billing')) return { page: 'Account', section: 'Billing', path: pathname };
  if (pathname.startsWith('/account')) return { page: 'Account', section: 'Settings', path: pathname };
  if (pathname.startsWith('/contributor')) return { page: 'Contributor', section: 'Dashboard', path: pathname };
  // Work items — extract document ID from path
  const workDocMatch = pathname.match(/^\/work\/(doc|sheet|slides|brainstorm|collective|page)\/([^/]+)/);
  if (workDocMatch) {
    const typeNames = { doc: 'Document', sheet: 'Spreadsheet', slides: 'Presentation', brainstorm: 'Brainstorm', collective: 'Collective Document', page: 'Page' };
    return { page: 'Work', section: typeNames[workDocMatch[1]] || workDocMatch[1], path: pathname, itemId: workDocMatch[2], itemType: workDocMatch[1] };
  }
  if (pathname.startsWith('/work')) return { page: 'Work', section: 'Files', path: pathname };
  // Sales
  if (pathname.startsWith('/sales/contacts')) {
    const section = contactId ? `Contact #${contactId.substring(0, 8)}${tab ? ` / ${tab}` : ''}` : 'Contacts';
    return { page: 'Sales', section, path: pathname, itemId: contactId, itemType: contactId ? 'contact' : null };
  }
  if (pathname.startsWith('/sales/pipeline')) {
    const section = dealId ? `Deal #${dealId.substring(0, 8)}` : 'Pipeline';
    return { page: 'Sales', section, path: pathname, itemId: dealId, itemType: dealId ? 'deal' : null };
  }
  if (pathname.startsWith('/sales/tasks')) return { page: 'Sales', section: 'Tasks', path: pathname };
  if (pathname.startsWith('/sales/companies')) return { page: 'Sales', section: 'Companies', path: pathname };
  if (pathname.startsWith('/sales/campaigns')) return { page: 'Sales', section: 'Campaigns', path: pathname };
  if (pathname.startsWith('/sales/reports')) return { page: 'Sales', section: 'Reports', path: pathname };
  if (pathname.startsWith('/sales')) return { page: 'Sales', section: 'Dashboard', path: pathname };
  if (pathname === '/') return { page: 'Home', section: null, path: pathname };
  return { page: 'Home', section: null, path: pathname };
}

export default function ContributionWidget() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [attachments, setAttachments] = useState([]); // {name, dataUrl, type}
  const [isDragging, setIsDragging] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [domContext, setDomContext] = useState(null);
  const [reportContext, setReportContext] = useState(null); // hidden context attached to next message
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Track current screen context with deep link
  const searchParams = new URLSearchParams(location.search);
  const context = getPageContext(location.pathname, searchParams);
  const deepLink = `${window.location.origin}${location.pathname}${location.search}`;

  useEffect(() => {
    if (open && isAuthenticated && !profile) {
      apiGet('/contributor/profile').then(r => setProfile(r.data)).catch(() => {});
    }
  }, [open, isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // File/screenshot handling
  const processFiles = useCallback((files) => {
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachments(prev => [...prev, {
          name: file.name,
          dataUrl: e.target.result,
          type: file.type,
          size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Paste handler (Cmd+V screenshots)
  useEffect(() => {
    if (!open) return;
    function handlePaste(e) {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          files.push(item.getAsFile());
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        processFiles(files);
      }
    }
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [open, processFiles]);

  if (!isAuthenticated) return null;

  // Drag and drop handlers
  function handleDragOver(e) { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }
  function handleDragLeave(e) { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }
  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length > 0) processFiles(Array.from(e.dataTransfer.files));
  }

  function removeAttachment(idx) {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSend(e) {
    e?.preventDefault();
    const msg = input.trim();
    if ((!msg && attachments.length === 0) || loading) return;

    const userMsg = {
      role: 'user',
      content: msg || (attachments.length > 0 ? `[Screenshot: ${attachments.map(a => a.name).join(', ')}]` : ''),
      attachments: attachments.map(a => ({ name: a.name, dataUrl: a.dataUrl })),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    const currentAttachments = [...attachments];
    setAttachments([]);
    setLoading(true);

    try {
      const res = await apiPost('/contributor/agent/chat', {
        message: msg,
        conversationId,
        context: { ...context, deepLink },
        attachments: currentAttachments.map(a => ({ name: a.name, type: a.type, size: a.size })),
        domContext: domContext || undefined,
        reportContext: reportContext || undefined,
      });
      setDomContext(null);
      setReportContext(null);
      if (res.data) {
        setConversationId(res.data.conversationId);
        setMessages(prev => [...prev, { role: 'assistant', ...res.data.response }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Try again.' }]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(action) {
    if (action.type === 'navigate') {
      navigate(action.route);
      setOpen(false);
      if (action.selector) {
        setTimeout(() => {
          const el = document.querySelector(action.selector);
          if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-blue-400'); setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400'), 3000); }
        }, 500);
      }
    } else if (action.type === 'create_proposal') {
      setLoading(true);
      try {
        // Build title + description from conversation — user's actual text only
        const userMsgs = messages.filter(m => m.role === 'user');
        const lastUserMsg = userMsgs[userMsgs.length - 1];
        const description = (lastUserMsg?.content || lastUserMsg?.text || '').trim();
        const shortDesc = description.slice(0, 80) + (description.length > 80 ? '…' : '');
        const title = `${action.proposalType === 'bug' ? 'Bug' : 'Feature'}: ${context.page}${context.section ? ` / ${context.section}` : ''} — ${shortDesc}`;

        const res = await apiPost('/contributor/proposals', {
          type: action.proposalType || 'bug',
          title,
          description,
          category: action.committee || 'general',
          // Hidden context: location, deep link, DOM selection — not visible to user
          context: { ...context, deepLink },
          domContext: domContext || reportContext?.domContext || undefined,
        });

        if (res.success) {
          const pts = res.data?.pointsAwarded || 5;
          const status = res.data?.proposal?.status;
          const statusMsg = status === 'auto_fixing'
            ? "It's a small fix — I'll handle it directly."
            : "It's been sent to the Engineering committee for review.";
          setMessages(prev => [...prev, {
            role: 'assistant',
            text: `Bug filed! +${pts} pts earned. ${statusMsg}`,
          }]);
          setProfile(p => p ? { ...p, points: (p.points || 0) + pts } : p);
        } else {
          setMessages(prev => [...prev, { role: 'assistant', text: 'Something went wrong filing the bug. Try again.' }]);
        }
      } catch {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Failed to submit. Try again.' }]);
      } finally {
        setLoading(false);
      }
    }
  }

  // Auto-detect input type from text
  const detectInputType = (text) => {
    if (!text || text.length < 5) return null;
    const lower = text.toLowerCase();
    // Bug indicators
    if (/\b(bug|broken|doesn't work|not working|error|crash|wrong|issue|problem|fail|can't|cannot|stuck|blank|missing|disappear|404|500|freeze|hang)\b/.test(lower)) return 'bug';
    // Feature indicators
    if (/\b(should|could|would be nice|add|feature|suggest|improve|enhance|wish|want|need|please|idea|proposal|how about|what if|it would)\b/.test(lower)) return 'feature';
    // Question indicators
    if (/\b(how|what|where|why|when|can i|is there|does|help|explain|guide|tutorial|documentation)\b/.test(lower) || lower.endsWith('?')) return 'question';
    return null;
  };

  const detectedType = detectInputType(input);

  function handleQuickAction(type) {
    if (type === 'showWhere') {
      setOpen(false);
      setTimeout(() => setShowRegionSelector(true), 100);
      return;
    }
    // Set hidden context for the report type — don't pollute the input box
    setReportContext({ type, page: context.page, section: context.section, deepLink, path: context.path });
    setInput('');
  }

  function handleRegionCapture({ rect, elements, screenshotDataUrl }) {
    setShowRegionSelector(false);
    setOpen(true);

    // Add screenshot as attachment
    if (screenshotDataUrl) {
      setAttachments(prev => [...prev, { name: 'region-screenshot.png', dataUrl: screenshotDataUrl, type: 'image/png', size: screenshotDataUrl.length }]);
    }

    // Store DOM context as hidden metadata — not in the input box
    setDomContext(elements);
    setReportContext({
      type: 'bug',
      page: context.page,
      section: context.section,
      deepLink,
      path: context.path,
      domContext: elements,
      rect,
    });
    // Input stays empty — user describes the issue in their own words
  }

  const points = profile?.points || 0;
  const level = profile?.level || 'observer';

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          {points > 0 && (
            <span className="absolute -top-1 -right-1 bg-amber-400 text-amber-900 text-[9px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {points > 999 ? `${Math.floor(points/1000)}k` : points}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`fixed bottom-5 right-5 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] flex flex-col rounded-2xl border bg-white shadow-2xl overflow-hidden transition-all ${isDragging ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200'}`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-sm font-semibold text-white">Contribution Agent</h3>
              <p className="text-[10px] text-blue-200">
                {points} pts &middot; {level.charAt(0).toUpperCase() + level.slice(1)}
                &middot; {context.page}{context.section ? ` / ${context.section}` : ''}
              </p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Context bar */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 bg-gray-50/50 text-[11px] text-gray-500 flex-shrink-0">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            <span>Viewing: <strong>{context.page}</strong>{context.section ? ` → ${context.section}` : ''}</span>
          </div>

          {/* Quick actions */}
          <div className="flex gap-1.5 px-3 py-2 border-b border-gray-100 flex-shrink-0">
            {[
              { key: 'bug', label: 'Bug', icon: '🐛' },
              { key: 'feature', label: 'Suggest', icon: '💡' },
              { key: 'question', label: 'Ask', icon: '❓' },
              { key: 'showWhere', label: 'Show Where', icon: '🎯' },
            ].map(a => (
              <button key={a.key} onClick={() => handleQuickAction(a.key)}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 hover:border-gray-300 transition-colors">
                <span>{a.icon}</span> {a.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {isDragging && (
              <div className="flex items-center justify-center h-32 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50/50">
                <p className="text-sm text-blue-500 font-medium">Drop screenshot here</p>
              </div>
            )}

            {messages.length === 0 && !isDragging && (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-900">Hi {user?.displayName?.split(' ')[0] || 'there'}!</p>
                <p className="text-xs text-gray-500 mt-1 max-w-[260px] mx-auto">Report bugs, suggest features, or ask questions. Paste or drag screenshots for context. Every contribution earns ownership.</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] ${
                  msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}>
                  <div className="whitespace-pre-wrap">{msg.content || msg.text}</div>

                  {/* Inline screenshots */}
                  {msg.attachments?.length > 0 && (
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {msg.attachments.map((att, j) => (
                        <img key={j} src={att.dataUrl} alt={att.name}
                          className="max-w-[200px] max-h-[120px] rounded-lg border border-white/20 cursor-pointer hover:opacity-80"
                          onClick={() => window.open(att.dataUrl, '_blank')} />
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  {msg.actions?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {msg.actions.map((action, j) => (
                        <button key={j} onClick={() => handleAction(action)}
                          className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
                            action.type === 'navigate' ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}>
                          {action.type === 'navigate' && '→ '}{action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Attachment preview */}
          {attachments.length > 0 && (
            <div className="flex gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50/50 overflow-x-auto flex-shrink-0">
              {attachments.map((att, i) => (
                <div key={i} className="relative flex-shrink-0">
                  <img src={att.dataUrl} alt={att.name} className="h-14 w-auto rounded-lg border border-gray-200" />
                  <button onClick={() => removeAttachment(i)}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center hover:bg-red-600">
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Hidden context indicator */}
          {reportContext && (
            <div className="border-t border-gray-100 px-3 py-1.5 flex items-center gap-2 bg-blue-50/80 flex-shrink-0">
              <svg className="w-3 h-3 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" /><path strokeLinecap="round" strokeLinejoin="round" d="M10.172 13.828a4 4 0 015.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" /></svg>
              <span className="text-[10px] text-blue-600">
                Context attached: {context.page}{context.section ? ` / ${context.section}` : ''}
                {reportContext.domContext ? ' + selected area' : ''}
              </span>
              <button onClick={() => setReportContext(null)} className="ml-auto text-[10px] text-blue-400 hover:text-blue-600">Clear</button>
            </div>
          )}

          {/* Auto-detected type indicator */}
          {detectedType && input.length > 10 && (
            <div className="border-t border-gray-100 px-3 py-1.5 flex items-center gap-2 bg-gray-50/80 flex-shrink-0">
              <span className="text-[10px] text-gray-400">Detected:</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                detectedType === 'bug' ? 'bg-red-100 text-red-700' :
                detectedType === 'feature' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {detectedType === 'bug' ? '🐛 Bug Report' : detectedType === 'feature' ? '💡 Feature Request' : '❓ Question'}
              </span>
              {detectedType !== 'question' && (
                <button type="button" onClick={() => {
                  handleAction({ type: 'create_proposal', proposalType: detectedType, committee: detectedType === 'bug' ? 'engineering' : 'product' });
                }} disabled={loading}
                  className={`ml-auto text-[10px] font-semibold px-2.5 py-1 rounded-full text-white disabled:opacity-50 ${
                    detectedType === 'bug' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                  }`}>
                  Submit {detectedType === 'bug' ? 'Bug' : 'Feature'} →
                </button>
              )}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSend} className="border-t border-gray-200 px-3 py-2 flex items-center gap-2 flex-shrink-0">
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0" title="Attach screenshot">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => { processFiles(Array.from(e.target.files)); e.target.value = ''; }} />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe a bug, suggest a feature..."
              className="flex-1 text-sm border-0 outline-none placeholder:text-gray-400 min-w-0"
            />
            <button type="submit" disabled={(!input.trim() && attachments.length === 0) || loading}
              className="p-1.5 rounded-full bg-blue-600 text-white disabled:opacity-30 hover:bg-blue-700 transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </form>

          {/* Footer links */}
          <div className="border-t border-gray-100 px-3 py-1.5 flex items-center justify-between bg-gray-50/50 flex-shrink-0">
            <a href="/equity" target="_blank" rel="noopener" className="text-[11px] text-blue-600 hover:text-blue-800 font-medium">
              Ownership model →
            </a>
            {profile?.points > 0 && (
              <span className="text-[10px] text-gray-400">{profile.points} pts</span>
            )}
          </div>

          {!profile?.isContributor && (
            <div className="border-t border-gray-100 px-3 py-2 bg-amber-50 flex-shrink-0">
              <button
                onClick={async () => {
                  await apiPost('/contributor/register', {});
                  setProfile(p => ({ ...p, isContributor: true, level: 'contributor' }));
                }}
                className="text-[11px] font-medium text-amber-800 hover:text-amber-900">
                Become a Contributor → earn ownership in Jubly
              </button>
            </div>
          )}
        </div>
      )}
      {/* Region Selector overlay */}
      {showRegionSelector && (
        <Suspense fallback={null}>
          <RegionSelector
            onCapture={handleRegionCapture}
            onCancel={() => { setShowRegionSelector(false); setOpen(true); }}
          />
        </Suspense>
      )}
    </>
  );
}
